/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import contractCheckinStyle from "./contractCheckinStyle";
import * as loginActions from "../../../store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "../../../services/landingPagesList";
import { getProductData } from "../../../services/productService";
import Typography from "@mui/material/Typography";
import { FormProvider } from "../../../provider/Form/FormProvider";
import SpecialEnergyContractCheckinForm from "./SpecialEnergyContractCheckinForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "../../../services/reactQuery/reactQueryService";
import { Box } from "@mui/material";
import { getPartnerNameByIri } from "../../../constants/partnerIris";
import type { PartnerIri } from "../../../constants/partnerIris";

type SpecialEnergyContractCheckinProps = {
  color?: "default" | "grey";
  target?: string;
  idPrefix: string;
  checkinHeading?: string;
  partner: PartnerIri;
};

const SpecialEnergyContractCheckin = ({
  color = "default",
  target = "/servicewelt",
  idPrefix,
  checkinHeading = "Fall schildern und professionelle Hilfe erhalten",
  partner,
}: SpecialEnergyContractCheckinProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const initialValues = {
    providerType: "",
    reasonForDispute: "",
    email: "",
  };
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const productClassName = "providerContract";

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);

    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email);
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);
    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      prelead: {
        initialProduct: productClassName,
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    await loginActions.register(data);
    await createMutation.mutateAsync({
      uri: getProductData(productClassName, "apiUri"),
      data: {
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        reasonForDispute: values.reasonForDispute,
        providerType: values.providerType,
        landingPage: landingPage.keyword,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={[contractCheckinStyle.formPaper, contractCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} sx={contractCheckinStyle.scrollToAnchor} />
      <Typography variant={"h4"} sx={contractCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <SpecialEnergyContractCheckinForm isLoading={isLoading} emailErrorText={emailErrorText} setEmailErrorText={setEmailErrorText} />
      </FormProvider>
    </Paper>
  );
};

export default SpecialEnergyContractCheckin;
